import type {
  ActionFlowTopologySimulationProof,
  ActionFlowTopologySimulationStep,
} from "./actionflow-topology-simulation";

export const ACTIONFLOW_USER_OUTCOME_CONTRACT_SCHEMA_VERSION =
  "actionflow-user-outcome-contract.v1";

export const ACTIONFLOW_USER_OUTCOME_PROOF_SCHEMA_VERSION =
  "actionflow-user-outcome-proof.v1";

type JsonRecord = Record<string, unknown>;

export type ActionFlowUserOutcomeCriterionKind =
  | "simulation-ok"
  | "state-path-equals"
  | "state-path-exists"
  | "state-path-absent"
  | "state-path-length"
  | "step-status"
  | "resolved-binding-equals";

export interface ActionFlowUserOutcomeCriterion {
  id: string;
  label: string;
  kind: ActionFlowUserOutcomeCriterionKind;
  severity?: "blocker" | "warning";
  path?: string;
  expected?: unknown;
  length?: number;
  primitiveRef?: string;
  status?: ActionFlowTopologySimulationStep["status"];
  bindingKey?: string;
  evidenceRefs: string[];
}

export interface ActionFlowUserOutcomeContract {
  schemaVersion: typeof ACTIONFLOW_USER_OUTCOME_CONTRACT_SCHEMA_VERSION;
  scenarioId: string;
  userGoal: string;
  userValue: string;
  persona?: string;
  acceptanceCriteria: ActionFlowUserOutcomeCriterion[];
  evidenceRefs: string[];
}

export interface ActionFlowUserOutcomeEvaluation {
  criterionId: string;
  label: string;
  kind: ActionFlowUserOutcomeCriterionKind;
  severity: "blocker" | "warning";
  passed: boolean;
  expected?: unknown;
  actual?: unknown;
  evidenceRefs: string[];
  message?: string;
}

export interface ActionFlowUserOutcomeProof {
  schemaVersion: typeof ACTIONFLOW_USER_OUTCOME_PROOF_SCHEMA_VERSION;
  contractVersion: typeof ACTIONFLOW_USER_OUTCOME_CONTRACT_SCHEMA_VERSION;
  scenarioId: string;
  userGoal: string;
  userValue: string;
  persona?: string;
  evaluatedAt: string;
  ok: boolean;
  readiness: "ready" | "blocked";
  outcomes: ActionFlowUserOutcomeEvaluation[];
  blockingFailures: string[];
  simulationSummary: ActionFlowTopologySimulationProof["summary"];
  evidenceRefs: string[];
}

export interface BuildActionFlowUserOutcomeProofOptions {
  evaluatedAt?: string;
}

export function buildActionFlowUserOutcomeProof(
  contract: ActionFlowUserOutcomeContract,
  simulationProof: ActionFlowTopologySimulationProof,
  options: BuildActionFlowUserOutcomeProofOptions = {},
): ActionFlowUserOutcomeProof {
  const outcomes = contract.acceptanceCriteria.map((criterion) =>
    evaluateCriterion(criterion, simulationProof),
  );
  const blockingFailures = outcomes
    .filter((outcome) => !outcome.passed && outcome.severity === "blocker")
    .map((outcome) => outcome.message ?? outcome.label);

  if (!simulationProof.ok) {
    blockingFailures.unshift(
      simulationProof.errors[0] ??
        simulationProof.validationErrors[0] ??
        "ActionFlow simulation proof failed.",
    );
  }

  return {
    schemaVersion: ACTIONFLOW_USER_OUTCOME_PROOF_SCHEMA_VERSION,
    contractVersion: ACTIONFLOW_USER_OUTCOME_CONTRACT_SCHEMA_VERSION,
    scenarioId: contract.scenarioId,
    userGoal: contract.userGoal,
    userValue: contract.userValue,
    ...(contract.persona ? { persona: contract.persona } : {}),
    evaluatedAt: options.evaluatedAt ?? new Date().toISOString(),
    ok: blockingFailures.length === 0,
    readiness: blockingFailures.length === 0 ? "ready" : "blocked",
    outcomes,
    blockingFailures,
    simulationSummary: simulationProof.summary,
    evidenceRefs: [...contract.evidenceRefs],
  };
}

function evaluateCriterion(
  criterion: ActionFlowUserOutcomeCriterion,
  simulationProof: ActionFlowTopologySimulationProof,
): ActionFlowUserOutcomeEvaluation {
  const severity = criterion.severity ?? "blocker";
  const base = {
    criterionId: criterion.id,
    label: criterion.label,
    kind: criterion.kind,
    severity,
    evidenceRefs: criterion.evidenceRefs,
  } satisfies Omit<
    ActionFlowUserOutcomeEvaluation,
    "passed" | "actual" | "expected" | "message"
  >;

  switch (criterion.kind) {
    case "simulation-ok":
      return {
        ...base,
        passed: simulationProof.ok,
        expected: true,
        actual: simulationProof.ok,
        message: simulationProof.ok
          ? undefined
          : "ActionFlow simulation must pass before user outcome is ready.",
      };
    case "state-path-equals": {
      const actual = readPath(simulationProof.finalState, requiredPath(criterion));
      const passed = deepEqual(actual, criterion.expected);
      return {
        ...base,
        passed,
        expected: criterion.expected,
        actual,
        message: passed
          ? undefined
          : `${criterion.path} must equal ${formatValue(criterion.expected)}`,
      };
    }
    case "state-path-exists": {
      const actual = readPath(simulationProof.finalState, requiredPath(criterion));
      const passed = actual !== undefined;
      return {
        ...base,
        passed,
        actual,
        message: passed ? undefined : `${criterion.path} must exist`,
      };
    }
    case "state-path-absent": {
      const actual = readPath(simulationProof.finalState, requiredPath(criterion));
      const passed = actual === undefined;
      return {
        ...base,
        passed,
        actual,
        message: passed ? undefined : `${criterion.path} must be absent`,
      };
    }
    case "state-path-length": {
      const actual = readPath(simulationProof.finalState, requiredPath(criterion));
      const actualLength =
        Array.isArray(actual) || typeof actual === "string"
          ? actual.length
          : actual && typeof actual === "object"
            ? Object.keys(actual).length
            : undefined;
      const passed = actualLength === criterion.length;
      return {
        ...base,
        passed,
        expected: criterion.length,
        actual: actualLength,
        message: passed
          ? undefined
          : `${criterion.path} length must be ${criterion.length}`,
      };
    }
    case "step-status": {
      const step = findStep(simulationProof, requiredPrimitiveRef(criterion));
      const actual = step?.status;
      const passed = actual === criterion.status;
      return {
        ...base,
        passed,
        expected: criterion.status,
        actual,
        message: passed
          ? undefined
          : `${criterion.primitiveRef} status must be ${criterion.status}`,
      };
    }
    case "resolved-binding-equals": {
      const step = findStep(simulationProof, requiredPrimitiveRef(criterion));
      const bindingKey = requiredBindingKey(criterion);
      const actual = step?.resolvedBindings[bindingKey];
      const passed = deepEqual(actual, criterion.expected);
      return {
        ...base,
        passed,
        expected: criterion.expected,
        actual,
        message: passed
          ? undefined
          : `${criterion.primitiveRef}.${bindingKey} must equal ${formatValue(criterion.expected)}`,
      };
    }
  }
}

function findStep(
  simulationProof: ActionFlowTopologySimulationProof,
  primitiveRef: string,
): ActionFlowTopologySimulationStep | undefined {
  return simulationProof.steps.find((step) => step.primitiveRef === primitiveRef);
}

function requiredPath(criterion: ActionFlowUserOutcomeCriterion): string {
  if (!criterion.path) {
    throw new Error(`${criterion.id} requires path`);
  }
  return criterion.path;
}

function requiredPrimitiveRef(criterion: ActionFlowUserOutcomeCriterion): string {
  if (!criterion.primitiveRef) {
    throw new Error(`${criterion.id} requires primitiveRef`);
  }
  return criterion.primitiveRef;
}

function requiredBindingKey(criterion: ActionFlowUserOutcomeCriterion): string {
  if (!criterion.bindingKey) {
    throw new Error(`${criterion.id} requires bindingKey`);
  }
  return criterion.bindingKey;
}

function readPath(record: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((current, segment) => {
    if (!current || typeof current !== "object" || Array.isArray(current)) {
      return undefined;
    }
    return (current as JsonRecord)[segment];
  }, record);
}

function deepEqual(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function formatValue(value: unknown): string {
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}
